/**
 * Get log: an agent's own log, fetched into CosMon (server migration 063).
 *
 * Owner, 2026-09-28: "surely we build the agent to upload logs?" Asking
 * records a request and the relay passes it to the agent over the socket it
 * keeps open; the answer lands here within seconds when the host is connected,
 * or when it next connects (for an hour). Logs an agent sent by itself after a
 * rolled-back update are listed too.
 *
 * The same drawer serves Get NX survey (064): the agent reads the NX Media
 * Server on its host and sends each answer back as JSON, tokens removed. Same
 * rows, same path; only the request, the title and the rendering differ.
 */

import { Download, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Drawer } from './components/index.ts';
import { NxResultView } from './NxResults.tsx';
import type { HostLogins } from './nx-logins.ts';

interface LogRow {
  id: string;
  reason: 'requested' | 'rollback' | 'nx_survey';
  requestedAt: string;
  receivedAt: string | null;
  agentVersion: string | null;
  bytes: number | null;
}

interface LogFull extends LogRow {
  content: string | null;
}

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

export function AgentLogDrawer({
  agentId,
  label,
  onClose,
  kind = 'log',
  logins,
  onChangeLogin,
}: {
  agentId: string;
  label: string;
  onClose: () => void;
  /** `nx_survey`: Get NX survey (064) instead of the agent's log. */
  kind?: 'log' | 'nx_survey';
  /** The host's NX logins now, so a survey can say when they have changed since. */
  logins?: HostLogins | undefined;
  onChangeLogin?: (() => void) | undefined;
}): React.JSX.Element {
  const survey = kind === 'nx_survey';
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [shown, setShown] = useState<LogFull | null>(null);
  const [waiting, setWaiting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useCallback(async () => {
    const res = await api.get<{ logs: LogRow[] }>(`/api/agents/${agentId}/logs`);
    const mine = res.logs.filter((l) => (l.reason === 'nx_survey') === survey);
    setLogs(mine);
    return mine;
  }, [agentId, survey]);

  const show = useCallback(async (id: string) => {
    setShown(await api.get<LogFull>(`/api/agent-logs/${id}`));
  }, []);

  const ask = useCallback(async () => {
    setError(null);
    try {
      const res = await api.post<{ id: string }>(`/api/agents/${agentId}/${survey ? 'nx-survey' : 'logs'}`);
      setWaiting(res.id);
      await list();
    } catch (err) {
      setError(failure(err));
    }
  }, [agentId, list, survey]);

  // Opening the drawer asks: that is what the button was pressed for.
  useEffect(() => {
    void ask();
  }, [ask]);

  // Until it arrives, look every three seconds.
  useEffect(() => {
    if (waiting === null) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const rows = await list();
          const row = rows.find((r) => r.id === waiting);
          if (row?.receivedAt != null) {
            setWaiting(null);
            await show(row.id);
          }
        } catch (err) {
          setError(failure(err));
        }
      })();
    }, 3000);
    return () => clearInterval(timer);
  }, [waiting, list, show]);

  // A survey is stored as compact JSON; shown and saved indented.
  const shownText = (() => {
    if (shown?.content == null) return '';
    if (!survey) return shown.content;
    try {
      return JSON.stringify(JSON.parse(shown.content), null, 2);
    } catch {
      return shown.content;
    }
  })();

  const download = (): void => {
    if (shown?.content == null) return;
    const url = URL.createObjectURL(new Blob([shownText], { type: survey ? 'application/json' : 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${survey ? 'nx-survey' : 'cosmon-agent'}-${label}-${(shown.receivedAt ?? '').slice(0, 19).replace(/[:T]/g, '-')}.${survey ? 'json' : 'log'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={survey ? `NX survey — ${label}` : `Agent log — ${label}`}
      subtitle={
        survey
          ? "What the host's NX Media Server answers, read by the agent with the NX login attached to this host. Tokens and passwords removed."
          : "The agent's own lines: reports, updates, sessions, tunnel. Never its secret."
      }
      width="lg"
    >
      <div className="stack-tight">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {waiting !== null && (
          <Banner tone="info" title="Asked the agent">
            It answers within seconds when it is connected; otherwise when it next connects, for up to an hour.
          </Banner>
        )}
        <div className="row-tight">
          <Button size="sm" icon={<RefreshCw size={14} />} onClick={() => void ask()} disabled={waiting !== null}>
            Get a fresh copy
          </Button>
          {shown?.content != null && !survey && (
            <Button size="sm" icon={<Download size={14} />} onClick={download}>
              Download
            </Button>
          )}
        </div>
        {logs.length > 0 && (
          <ul className="agent-log-list small">
            {logs.map((l) => (
              <li key={l.id}>
                <button type="button" className="agent-log-pick" disabled={l.receivedAt === null} onClick={() => void show(l.id)}>
                  {new Date(l.requestedAt).toLocaleString()}
                </button>{' '}
                · {l.reason === 'rollback' ? 'sent after a rolled-back update' : 'asked for'}
                {l.agentVersion !== null && ` · agent ${l.agentVersion}`}
                {l.receivedAt === null ? ' · waiting' : l.bytes !== null ? ` · ${Math.ceil(l.bytes / 1024)} KB` : ''}
                {shown?.id === l.id && ' · shown'}
              </li>
            ))}
          </ul>
        )}
        {shown?.content != null &&
          (survey ? (
            <NxResultView raw={shown.content} label={label} logins={logins} onChangeLogin={onChangeLogin} />
          ) : (
            <pre className="agent-log" aria-label="Agent log" tabIndex={0}>
              {shownText}
            </pre>
          ))}
      </div>
    </Drawer>
  );
}
